import { useState, useId, useEffect } from 'react';
import { units } from '@ui/css';
import { throttleToAnimationFrame } from '@utils/common';
import { applyCssDeclarations } from '@utils/css';
import { makeSizeTokens } from './size.selectors.js';

const currentIdCache = new WeakMap();
function useElementSize(ref, args, ...extraDeps) {
  const [width, setWidth] = useState(undefined);
  const [height, setHeight] = useState(undefined);
  const id = useId();
  useEffect(() => {
    const target = ref && "current" in ref ? ref?.current : ref;
    const enabled = args.enabled ?? true;
    if (!target || !enabled) {
      return;
    }
    const resolvedTarget = (args.applyTo && "current" in args.applyTo ? args.applyTo.current : args.applyTo) ?? target;
    if (resolvedTarget) {
      currentIdCache.set(resolvedTarget, {
        ...currentIdCache.get(resolvedTarget),
        [args.varName ?? "--element-size"]: id
      });
    }
    let prevWidth;
    let prevHeight;
    const onResize = () => {
      const isActive = getCurrentlyActiveIdForTarget(args.applyTo ?? ref, args.varName) === id;
      if (!isActive || !target.isConnected) {
        return;
      }
      const nextWidth = target.clientWidth;
      const nextHeight = target.clientHeight;
      if (args.axis === "horisontal" && nextWidth === prevWidth || args.axis === "vertical" && nextHeight === prevHeight || args.axis === "both" && nextHeight === prevHeight && nextWidth === prevWidth) {
        return;
      }
      applyCssDeclarations(makeSizeTokens(args.varName ?? "--element-size").__override({
        height: units.px(nextHeight),
        width: units.px(nextWidth)
      }), args.applyTo ?? target);
      if (args.updateState) {
        setWidth(nextWidth);
        setHeight(nextHeight);
      }
      prevWidth = nextWidth;
      prevHeight = nextHeight;
    };
    const observer = new ResizeObserver(throttleToAnimationFrame(onResize));
    observer.observe(target);
    onResize();
    return () => {
      observer.disconnect();
      const isActive = getCurrentlyActiveIdForTarget(args.applyTo ?? ref, args.varName) === id;
      if (!isActive) {
        return;
      }
      applyCssDeclarations({
        [makeSizeTokens(args.varName ?? "--element-size").height.propName]: null,
        [makeSizeTokens(args.varName ?? "--element-size").width.propName]: null
      }, args.applyTo ?? target);
    };
  }, [args.applyTo, args.axis, args.enabled, args.updateState, args.varName, id, ref,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ...extraDeps]);
  return {
    width,
    height
  };
}
function getCurrentlyActiveIdForTarget(target, varName) {
  const resolvedTarget = target && "current" in target ? target.current : target;
  if (!resolvedTarget) {
    return undefined;
  }
  return currentIdCache.get(resolvedTarget)?.[varName ?? "--element-size"];
}

export { useElementSize };
//# sourceMappingURL=size.js.map
